// types
import dayjs from 'dayjs';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';
import type { OpenRequestStatusCategory } from 'RestAPIs/quality-monitor';
import type {
  BreakdownRow,
  CostCenterRow,
  OpenRequestRow,
  TotalActionsEntry
} from './OpenRequests.types';

const OPEN_REQUEST_STATUS_CATEGORIES: OpenRequestStatusCategory[] = [
  'OPEN',
  'IN_ROSTER_MATCH',
  'SENT_TO_FLEX',
  'OPEN_FOR_SELF_MATCH',
  'PARKED'
];

export const OPEN_REQUEST_STATUS_ICONS: Record<
  OpenRequestStatusCategory,
  IconType
> = {
  OPEN: 'circle-exclamation',
  IN_ROSTER_MATCH: 'comment',
  SENT_TO_FLEX: 'right-left',
  OPEN_FOR_SELF_MATCH: 'users',
  PARKED: 'ban'
};

export const buildRows = (
  entries: TotalActionsEntry[],
  expandedIds: Set<number>
): OpenRequestRow[] =>
  entries.flatMap((entry, index) => {
    const striped = index % 2 === 0;

    const categories = OPEN_REQUEST_STATUS_CATEGORIES.filter((category) =>
      entry.days.some((day) => (day.actionsPerStatus?.[category] || 0) > 0)
    );

    const costCenterRow: CostCenterRow = {
      type: 'costCenter',
      costCenterId: entry.costCenterId,
      costCenterName: entry.costCenterName,
      striped,
      hasBreakdown: categories.length > 0,
      days: entry.days.map((day) => ({
        date: day.date,
        count: day.totalNumberOfActions || 0
      }))
    };

    if (!expandedIds.has(entry.costCenterId)) {
      return [costCenterRow];
    }

    const breakdownRows: BreakdownRow[] = categories.map((category) => ({
      type: 'breakdown',
      costCenterId: entry.costCenterId,
      category,
      striped,
      days: entry.days.map((day) => ({
        date: day.date,
        count: day.actionsPerStatus?.[category] || 0
      }))
    }));

    return [costCenterRow, ...breakdownRows];
  });

export const getRowKey = (row: OpenRequestRow): string =>
  row.type === 'costCenter'
    ? `costCenter-${row.costCenterId}`
    : `breakdown-${row.costCenterId}-${row.category}`;

export const countTotalActions = (entries: TotalActionsEntry[]): number => {
  return entries.reduce((total, entry) => {
    const dailyTotal = entry.days.reduce(
      (sum, day) => sum + (day.totalNumberOfActions || 0),
      0
    );
    return total + dailyTotal;
  }, 0);
};

export const formatShortDate = (
  date: string | Date | undefined,
  subtractDays = 0
) => dayjs(date).subtract(subtractDays, 'day').format('DD MMM');
